import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";

const shellStyle = { paddingTop: 120, paddingBottom: 80 };
const boxStyle = { maxWidth: 480, margin: "0 auto", padding: "0 20px" };

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      setSent(true);  // Silent — don't reveal which emails exist
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-shell">
      <Navbar />
      <main style={shellStyle}>
        <div style={boxStyle}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Forgotten password</div>
          <h1 className="editorial-h1" style={{ fontSize: 42, marginTop: 12, marginBottom: 20 }}>
            {sent ? "Letter sent." : "It happens."}
          </h1>
          {sent ? (
            <p className="editorial-body" style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-secondary)", fontStyle: "italic" }}>
              If an account exists for that email, a reset link is on its way. The link is valid for 2 hours.
              You may close this page and check your inbox.
            </p>
          ) : (
            <form onSubmit={submit}>
              <p className="editorial-body" style={{ fontSize: 15, color: "var(--text-secondary)", marginBottom: 20 }}>
                Enter the email on your account. We'll send a link to choose a new password.
              </p>
              <input
                type="email" required autoFocus
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                autoComplete="email" inputMode="email"
                className="input-belle"
                data-testid="forgot-email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                style={{ width: "100%", marginBottom: 20 }}
              />
              <button type="submit" disabled={busy} data-testid="send-reset-link" className="btn-primary" style={{ width: "100%" }}>
                {busy ? "Sending…" : "Send reset link"}
              </button>
              <p style={{ marginTop: 20, textAlign: "center" }}>
                <Link to="/login" style={{ fontSize: 13, color: "var(--text-muted)" }}>Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const p1 = pw.trim();
    const p2 = pw2.trim();
    if (p1.length < 8) return toast.error("Password must be at least 8 characters.");
    if (p1 !== p2) return toast.error("The two passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: p1 });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-shell">
      <Navbar />
      <main style={shellStyle}>
        <div style={boxStyle}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Reset password</div>
          <h1 className="editorial-h1" style={{ fontSize: 42, marginTop: 12, marginBottom: 20 }}>Choose a new one.</h1>
          <form onSubmit={submit}>
            <label className="overline">New password</label>
            <input type="password" required minLength={8}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              autoComplete="new-password"
              className="input-belle" data-testid="new-password"
              value={pw} onChange={(e) => setPw(e.target.value)} style={{ width: "100%", marginBottom: 16 }} />
            <label className="overline">Confirm new password</label>
            <input type="password" required minLength={8}
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              autoComplete="new-password"
              className="input-belle" data-testid="confirm-password"
              value={pw2} onChange={(e) => setPw2(e.target.value)} style={{ width: "100%", marginBottom: 20 }} />
            <button type="submit" disabled={busy} data-testid="submit-reset" className="btn-primary" style={{ width: "100%" }}>
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const VerifyEmail = () => {
  const { token } = useParams();
  const [state, setState] = useState("checking");
  const attempted = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invocation (would cause the
    // second call to see the token as already used and mistakenly show
    // "Link no longer valid" even though verification succeeded).
    if (attempted.current) return;
    attempted.current = true;
    api.post("/auth/verify-email", { token })
      .then(() => setState("verified"))
      .catch(() => setState("failed"));
  }, [token]);

  return (
    <div className="app-shell">
      <Navbar />
      <main style={shellStyle}>
        <div style={boxStyle}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Email confirmation</div>
          <h1 className="editorial-h1" style={{ fontSize: 42, marginTop: 12, marginBottom: 20 }}>
            {state === "checking" && "One moment…"}
            {state === "verified" && "Confirmed."}
            {state === "failed" && "Link no longer valid."}
          </h1>
          <p className="editorial-body" style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-secondary)", fontStyle: "italic" }}>
            {state === "checking" && "Confirming your letter box…"}
            {state === "verified" && "Your email is confirmed. Welcome to Le Salon."}
            {state === "failed" && "This confirmation link has expired or was already used. Go to Settings to request a fresh one."}
          </p>
          <p style={{ marginTop: 24 }}>
            <Link to="/feed" className="btn-primary" style={{ textDecoration: "none", display: "inline-block" }}>Continue</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};
